/**
 * MappingStep Component
 *
 * Step 2 of the CSV import wizard. Shows column mapping UI for each uploaded file
 * as collapsible sections. Users can map CSV columns to CRM properties and optionally
 * define cross-file associations.
 *
 * Sub-components (FileMappingRow, MultiTypeMapping, etc.) live in
 * the mapping-step/ subdirectory to keep this file focused on orchestration.
 */

import { Link01, PlusCircle } from '@untitledui/icons';
import type { Dispatch, Key, ReactNode, SetStateAction } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { usePermissions } from '@/hooks/usePermissions';
import { Button } from '@/lib/base/buttons/button';
import { Select } from '@/lib/base/forms/select/select';
import { SelectItem } from '@/lib/base/forms/select/select-item';
import { Skeleton } from '@/lib/base/skeleton';
import type { PropertyFormValues } from '@/lib/crm/components/properties/PropertyFormFields';
import { useAssociationMatchers } from '@/lib/crm/hooks/useAssociationMatchers';
import type {
  AssociationMapping,
  AssociationSuggestion,
  ColumnMappings,
  CrmEntityAssociationMapping,
  FixedValueMapping,
  MultiTypeMappingSuggestion,
  NewPropertySuggestion,
  SelectOptionSuggestion,
  StageMappings,
  UploadedFileEntry,
} from '@/lib/csv-import/api';
import { logger } from '@/lib/logger';
import { cx } from '@/utils/cx';

import {
  type AssociationTypeSelections,
  getEmittableEntityTypeNames,
  getRenderedEntityTypeNames,
} from '../csv-import-helpers';
import {
  useImportableEntityTypes,
  useImportTargetTypes,
} from '../hooks/useImportTargetTypes';
import { useImportVocabulary } from '../import-vocabulary';
import { SKIP_MARKER } from '../useCsvImport';
import { getEntityTypeLabel } from '../utils/entityTypeLabels';
import { CrossFileAssociations } from './mapping-step/CrossFileAssociations';
import { EntityPreviewCard } from './mapping-step/EntityPreviewCard';
import { FileSidebarRow } from './mapping-step/FileSidebarRow';
import type {
  ActiveView,
  FileColumnInfo,
} from './mapping-step/mapping-constants';
import {
  CRM_ENTITY_LOOKUP_PROPERTIES,
  propertyIndexKey,
} from './mapping-step/mapping-constants';
import { MappingOutcomesLegend } from './mapping-step/MappingOutcomesLegend';
import { MultiTypeMapping } from './mapping-step/MultiTypeMapping';
import type {
  NewPropertyItem,
  NewPropertyUsage,
} from './mapping-step/NewPropertiesPanel';
import { NewPropertiesPanel } from './mapping-step/NewPropertiesPanel';

// ============================================================================
// MappingStepProps
// ============================================================================

interface MappingStepProps {
  orgId: number;
  uploadedFiles: UploadedFileEntry[];
  columnMappings: ColumnMappings;
  onColumnMappingsChange: (mappings: ColumnMappings) => void;
  /** CSV columns per file, keyed by file ID */
  fileColumns: Record<number, string[]>;
  /** AI-suggested associations per file, keyed by file ID */
  suggestedAssociations?: Record<string, AssociationSuggestion[]>;
  /** Current stage mappings per file, keyed by file ID string */
  stageMappings: StageMappings;
  /** Called when stage mappings change */
  onStageMappingsChange: Dispatch<SetStateAction<StageMappings>>;
  /** Per-section columns toggled on as unique-match columns, keyed by section id. */
  matchColumns: Record<string, string[]>;
  /** Called when a column's record-matching selection changes. */
  onMatchColumnToggle: (
    fileId: string | number,
    csvColumn: string,
    enabled: boolean
  ) => void;
  /** Per-file multi-type suggestions, keyed by real file ID */
  multiTypeSuggestions: Record<string | number, MultiTypeMappingSuggestion>;
  /** Called when a new property's field type changes in multi-type mode */
  onNewPropertyFieldTypeChange?: (
    fileId: string | number,
    entityTypeName: string,
    propertyName: string,
    newFieldType: string
  ) => void;
  /**
   * Per-file association-type selections: real fileId → directed pair key
   * (`${sourceType}|${targetType}`) → chosen association type name ('' = the
   * user disabled the link; absent = default to the first valid type).
   */
  associationTypeSelections: AssociationTypeSelections;
  /** Called when the user picks (or disables) the association type for a directed
   * section pair (source = the row above, target = the row below). */
  onAssociationTypeSelectionChange: (
    fileId: string | number,
    sourceType: string,
    targetType: string,
    associationTypeName: string
  ) => void;
  /**
   * Called when the user wants to add a custom property from the mapping
   * dropdown. `mappingKey` is the key the originating row reads its column
   * mappings from — the multi-type virtual editKey (`${fileId}_virtual_${type}`)
   * or the plain file id — supplied by the section that owns the row.
   */
  onCreateProperty?: (
    mappingKey: string | number,
    csvColumn: string,
    label: string
  ) => void;
  /** User-created new properties per file (keyed by file ID string). */
  userNewProperties?: Record<string, NewPropertySuggestion[]>;
  /** Called when a user-created new property's field type changes in standard (non-multi-type) mode. */
  onUserNewPropertyFieldTypeChange?: (
    fileId: string | number,
    propertyName: string,
    entityTypeName: string,
    newFieldType: string
  ) => void;
  /** Called when a user-created new property's select options change. */
  onUserNewPropertyOptionsChange?: (
    fileId: string | number,
    propertyName: string,
    entityTypeName: string,
    options: SelectOptionSuggestion[]
  ) => void;
  /** Called when a user-created new property's label changes in the panel. */
  onUserNewPropertyLabelChange?: (
    fileId: string | number,
    propertyName: string,
    entityTypeName: string,
    newLabel: string
  ) => void;
  /**
   * Regenerate a select property's options from the CSV ("Generate options from
   * CSV"). Owned by usePropertyManagement so the fetch shares the options
   * generation guard and a late response can't clobber a newer user edit.
   */
  onGenerateSelectOptions?: (
    fileId: string | number,
    propertyName: string,
    entityTypeName: string
  ) => void;
  /** Sample CSV rows per file (keyed by real file ID), for entity previews. */
  fileSampleRows?: Record<string, Record<string, string>[]>;
  /** Original file objects (or CSV-converted blobs for XLSX) for streaming search. */
  fileObjects?: Record<string, File | Blob>;
  /** Row counts per file (keyed by file ID), for preview context. */
  fileRowCounts?: Record<string, number>;
  /** Mappings from CSV columns to existing CRM entities (e.g. owner associations). */
  crmEntityAssociationMappings: CrmEntityAssociationMapping[];
  /** Called when CRM entity association mappings change. */
  onCrmEntityAssociationMappingsChange: (
    mappings: CrmEntityAssociationMapping[]
  ) => void;
  /** Per-file fixed association defaults (owner, note visibility). */
  fixedValueMappings: FixedValueMapping[];
  /** Called when fixed value mappings change. */
  onFixedValueMappingsChange: (mappings: FixedValueMapping[]) => void;
  /**
   * Validation errors from the most recent analysis, keyed by file ID then
   * property name → example error message. When present, the mapping rows
   * for these properties are highlighted with a tooltip showing the error.
   */
  analysisErrorProperties?: Record<string, Record<string, string>>;
  /**
   * Registers an entity type in a file's data model so its mapped columns are
   * submitted. Fired automatically when a column is dragged onto a type's pill.
   */
  onAddEntityType?: (fileId: string | number, entityTypeName: string) => void;
  /** Cross-file value-match association mappings. */
  associationMappings?: AssociationMapping[];
  /** Called when cross-file association mappings change. */
  onAssociationMappingsChange?: (mappings: AssociationMapping[]) => void;
}

// ============================================================================
// MappingDetailSkeleton — shown while the detail pane has no content yet
// ============================================================================

/** Skeleton placeholder for the mapping detail pane (entity section + table rows). */
function MappingDetailSkeleton() {
  return (
    <div className='space-y-4' aria-busy='true'>
      {/* Entity type header */}
      <div className='flex items-center gap-2'>
        <Skeleton variant='circular' width={28} height={28} />
        <Skeleton variant='text' width={140} height={20} />
      </div>
      {/* Table header row */}
      <div className='flex gap-4 pt-2'>
        <Skeleton variant='text' width='30%' height={14} />
        <Skeleton variant='text' width='40%' height={14} />
        <Skeleton variant='text' width='20%' height={14} />
      </div>
      {/* Table body rows */}
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className='flex items-center gap-4'>
          <Skeleton variant='text' width='30%' height={20} />
          <Skeleton variant='rectangular' width='40%' height={36} />
          <Skeleton variant='text' width='20%' height={20} />
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function MappingStep({
  orgId,
  uploadedFiles,
  columnMappings,
  onColumnMappingsChange,
  fileColumns,
  suggestedAssociations,
  stageMappings,
  onStageMappingsChange,
  matchColumns,
  onMatchColumnToggle,
  multiTypeSuggestions,
  onNewPropertyFieldTypeChange,
  associationTypeSelections,
  onAssociationTypeSelectionChange,
  onCreateProperty,
  userNewProperties: userNewPropertiesProp,
  onUserNewPropertyFieldTypeChange,
  onUserNewPropertyOptionsChange,
  onUserNewPropertyLabelChange,
  onGenerateSelectOptions,
  fileSampleRows,
  fileObjects,
  fileRowCounts: fileRowCountsProp,
  crmEntityAssociationMappings,
  onCrmEntityAssociationMappingsChange,
  fixedValueMappings,
  onFixedValueMappingsChange,
  analysisErrorProperties,
  onAddEntityType,
  associationMappings,
  onAssociationMappingsChange,
}: MappingStepProps) {
  // The sidebar lists files for an upload, but entity types for a source-driven
  // import — where "Files" would be naming something that doesn't exist.
  const vocabulary = useImportVocabulary();

  const { isSchemaAdmin } = usePermissions();
  const targetTypeOptions = useImportTargetTypes(orgId);
  // Entity types the importing user can create — the set offered as importable
  // "slots" (pills) in the mapping view. Association targets stay on the full
  // targetTypeOptions set (linking to existing records is a read concern).
  const importableTypeOptions = useImportableEntityTypes(orgId);
  // Kind-aware association-type resolution for the per-column "Link to record"
  // config (drives the conditional "As" selector). Cached + localStorage-backed.
  const { associationTypes, matchesSource, matchesTarget } =
    useAssociationMatchers(orgId);
  // Sidebar selection — what the detail pane is currently showing. The legacy
  // multi-accordion-open state has been replaced with single-item selection.
  // Eagerly initialize to the first file so the detail pane renders on mount
  // instead of showing a blank frame until the useEffect fires.
  const [activeView, setActiveView] = useState<ActiveView | null>(() =>
    uploadedFiles.length > 0
      ? { kind: 'file', fileId: uploadedFiles[0].file_id }
      : null
  );
  const [hasAutoPopulatedCrmAssoc, setHasAutoPopulatedCrmAssoc] =
    useState(false);

  // Build file info objects combining uploaded files with column data
  const files: FileColumnInfo[] = useMemo(
    () =>
      uploadedFiles.map(f => {
        const fileIdStr: string = String(f.file_id);
        return {
          fileId: f.file_id,
          filename: f.filename,
          entityTypeName: f.entity_type_name,
          columns: (fileColumns as Record<string, string[]>)[fileIdStr] ?? [],
        };
      }),
    [uploadedFiles, fileColumns]
  );

  // Per-file set of the entity types currently presented as sections, derived
  // from live column mappings via the shared helper. Single source of truth for
  // "what types does this file have right now" — sidebar label, preview cards,
  // CRM-association gating and the unmapped-columns warning all read from here,
  // so a type added/emptied in the mapping pane stays consistent everywhere.
  const renderedTypesByFile = useMemo(() => {
    const map: Record<string, Set<string>> = {};
    for (const file of files) {
      const suggestion = multiTypeSuggestions[file.fileId];
      if (suggestion) {
        map[String(file.fileId)] = new Set(
          getRenderedEntityTypeNames(suggestion, columnMappings, file.fileId)
        );
      }
    }
    return map;
  }, [files, multiTypeSuggestions, columnMappings]);

  // Files whose DECLARED type currently emits no importable section — an
  // association endpoint on them would be dropped at submit (validateMappings
  // blocks Next for the same reason), so the association editor warns on rows
  // that reference them. Same emit mirror as validateMappings/buildSubmitPayload.
  const unlinkableFileIds = useMemo(() => {
    const ids = new Set<string>();
    for (const file of uploadedFiles) {
      const suggestion = multiTypeSuggestions[file.file_id];
      if (!suggestion) {
        continue;
      }
      const emittable = getEmittableEntityTypeNames(
        file.file_id,
        suggestion,
        columnMappings,
        stageMappings,
        crmEntityAssociationMappings
      );
      if (!emittable.has(file.entity_type_name)) {
        ids.add(String(file.file_id));
      }
    }
    return ids;
  }, [
    uploadedFiles,
    multiTypeSuggestions,
    columnMappings,
    stageMappings,
    crmEntityAssociationMappings,
  ]);

  // Default sidebar selection: the first file once files are available. Only
  // runs while activeView is still null so the user's later selection sticks.
  useEffect(() => {
    if (activeView !== null) {
      return;
    }
    if (files.length > 0) {
      setActiveView({ kind: 'file', fileId: files[0].fileId });
    }
  }, [activeView, files]);

  // Snap selection back to a valid file if the current one disappears (e.g.
  // a multi-type suggestion was cleared, removing the virtual entries).
  useEffect(() => {
    if (activeView?.kind !== 'file') {
      return;
    }
    const stillExists = files.some(f => f.fileId === activeView.fileId);
    if (stillExists) {
      return;
    }
    if (files.length > 0) {
      setActiveView({ kind: 'file', fileId: files[0].fileId });
    } else {
      setActiveView(null);
    }
  }, [activeView, files]);

  // Auto-populate CRM object association suggestions from LLM-suggested
  // associations. Unlike cross-file associations (which link between uploaded
  // CSVs), CRM object associations match a CSV column against records already
  // in the CRM (e.g. "Owner Email" → look up ahoy_user by ahoy_email).
  // These are suggested regardless of whether a cross-file match also exists —
  // both can coexist (cross-file links uploaded rows, CRM entity links to
  // existing records for rows that don't match an uploaded file).
  useEffect(() => {
    if (
      hasAutoPopulatedCrmAssoc ||
      !suggestedAssociations ||
      crmEntityAssociationMappings.length > 0
    ) {
      return;
    }

    const newCrmAssociations: CrmEntityAssociationMapping[] = [];

    for (const [sourceFileId, suggestions] of Object.entries(
      suggestedAssociations
    )) {
      // Skip GENUINELY multi-type source files (more than one rendered entity-
      // type section) — their suggestions don't map cleanly to a single real
      // file ID. Post-consolidation every file has a multiTypeSuggestions entry
      // (single-type files are synthesized into a one-section suggestion), so
      // gating on mere presence would wrongly skip every file. We gate on the
      // number of RENDERED types so a file that dropped back to a single section
      // still gets CRM association auto-population.
      const renderedSource = renderedTypesByFile[sourceFileId];
      if (renderedSource !== undefined && renderedSource.size > 1) {
        continue;
      }

      for (const suggestion of suggestions) {
        const { target_entity_type_name: targetType } = suggestion;
        const targetProperty = suggestion.target_property;

        // Only suggest when the target type + property are valid CRM entity
        // lookup combinations (e.g. ahoy_company + ahoy_name).
        const validProperties = CRM_ENTITY_LOOKUP_PROPERTIES[targetType];
        if (!validProperties) {
          continue;
        }
        const isValidProperty = validProperties.some(
          p => p.value === targetProperty
        );
        if (!isValidProperty) {
          continue;
        }

        newCrmAssociations.push({
          source_file_id: Number(sourceFileId),
          source_column: suggestion.column,
          target_entity_type_name: targetType,
          target_property: targetProperty,
        });
      }
    }

    if (newCrmAssociations.length > 0) {
      logger.info(
        'Auto-populating CRM object associations from LLM suggestions',
        {
          component: 'csv-import',
          count: newCrmAssociations.length,
        }
      );
      onCrmEntityAssociationMappingsChange(newCrmAssociations);
    }

    setHasAutoPopulatedCrmAssoc(true);
  }, [
    suggestedAssociations,
    renderedTypesByFile,
    crmEntityAssociationMappings.length,
    hasAutoPopulatedCrmAssoc,
    onCrmEntityAssociationMappingsChange,
  ]);

  const handleSelectFile = useCallback((fileId: string | number) => {
    setActiveView({ kind: 'file', fileId });
  }, []);

  const handleSelectNewProperties = useCallback(() => {
    setActiveView({ kind: 'newProperties' });
  }, []);

  const handleSelectAssociations = useCallback(() => {
    setActiveView({ kind: 'associations' });
  }, []);

  // Property (name + entity type) to auto-expand when navigating to
  // NewPropertiesPanel via "Configure options →". Cleared when the user leaves
  // the New Properties view (see the effect below) so re-entering it via the
  // sidebar — without a fresh configure click — does not re-expand and re-scroll
  // to a stale card. Entity type disambiguates two entity types that
  // independently derive the same property-name slug.
  const [expandPropertyName, setExpandPropertyName] = useState<string | null>(
    null
  );
  const [expandEntityTypeName, setExpandEntityTypeName] = useState<
    string | null
  >(null);

  // Navigate to the NewPropertiesPanel when a user clicks "Configure options →"
  // on a select-type property that has no options yet. Also stores the target
  // property name + entity type so the panel auto-expands and scrolls to it.
  const handleConfigureSelectOptions = useCallback(
    (propertyName: string, entityTypeName: string) => {
      setExpandPropertyName(propertyName);
      setExpandEntityTypeName(entityTypeName);
      setActiveView({ kind: 'newProperties' });
    },
    []
  );

  // Clear the auto-expand target whenever the active view is not the New
  // Properties panel. handleConfigureSelectOptions sets these states together
  // with activeView='newProperties', so this effect leaves them intact while
  // that panel is open (and the panel finishes its expand + scroll). The moment
  // the user navigates elsewhere the panel unmounts and the target is dropped,
  // so a later sidebar return to New Properties mounts with null props and does
  // not re-expand the old card. Clearing on leave (rather than via a panel
  // mount callback) also sidesteps a race where resetting mid-mount would cancel
  // the card's pending scroll-into-view timer.
  useEffect(() => {
    if (activeView?.kind !== 'newProperties') {
      setExpandPropertyName(null);
      setExpandEntityTypeName(null);
    }
  }, [activeView?.kind]);

  // Collect all new properties that will be created across all files.
  // User-created/edited properties take priority over AI-suggested so field
  // type edits persist. Deduplicated by (property_name, entity_type_name) —
  // two entity types in a multi-type file can independently derive the same
  // property-name slug and must render as distinct cards — but NOT by file:
  // the import creates exactly one property per (property, entity type)
  // (backend `collect_new_property_specs` merges the per-file declarations),
  // so two files defining the same one render as a single card whose edits
  // fan out to every file's entry (see `fileIds` / handlePanelPropertyChange).
  const allNewProperties = useMemo(() => {
    const cardsByKey = new Map<string, NewPropertyItem>();
    const result: NewPropertyItem[] = [];

    // A new property is only "live" while a column is actively mapped to it
    // under its OWN entity type. When the user declines a suggestion (remaps
    // that column to an existing property, skips it, or links it), it drops out
    // of the panel. Liveness is keyed by (property_name, entity_type_name) — not
    // by slug alone — so removing one entity type's mapping cannot keep a
    // sibling card live when two entity types in a multi-type file derive the
    // same slug. The usagesByProperty map built below is exactly this liveness
    // index: it is keyed by that same composite and populated from the same
    // active (non-skip) column mappings, so a property is live iff it has at
    // least one usage.

    // Build reverse map: propertyIndexKey(propertyName, entityTypeName) → all
    // (fileKey, column) usages from columnMappings so we can show provenance
    // in the New Properties panel. Keyed on the composite so a usage under one
    // entity type doesn't bleed into another entity type's card when they
    // share a property-name slug.
    const usagesByProperty = new Map<string, NewPropertyUsage[]>();
    for (const [fileKey, fileMap] of Object.entries(columnMappings)) {
      for (const [column, propertyName] of Object.entries(fileMap)) {
        if (!propertyName || propertyName === SKIP_MARKER) {
          continue;
        }

        // Resolve fileKey → filename, entity type, and real file ID. Virtual
        // keys use the pattern "<fileId>_virtual_<entityTypeName>".
        const virtualMatch = fileKey.match(/^(.+)_virtual_(.+)$/);
        let filename: string;
        let entityTypeName: string;
        let realFileId: string | number;

        if (virtualMatch) {
          realFileId = virtualMatch[1];
          entityTypeName = virtualMatch[2];
          const realFile = uploadedFiles.find(
            f => String(f.file_id) === realFileId
          );
          filename = realFile?.filename ?? `File ${realFileId}`;
          // Preserve the original file_id type (number) when possible
          if (realFile) {
            realFileId = realFile.file_id;
          }
        } else {
          const file = uploadedFiles.find(f => String(f.file_id) === fileKey);
          filename = file?.filename ?? `File ${fileKey}`;
          entityTypeName = file?.entity_type_name ?? '';
          realFileId = file?.file_id ?? fileKey;
        }

        const usageKey = propertyIndexKey(propertyName, entityTypeName);
        let usages = usagesByProperty.get(usageKey);
        if (!usages) {
          usages = [];
          usagesByProperty.set(usageKey, usages);
        }
        usages.push({ fileId: realFileId, filename, entityTypeName, column });
      }
    }

    const pushIfLive = (np: NewPropertySuggestion, fileId: string | number) => {
      const key = propertyIndexKey(np.property_name, np.entity_type_name);
      if (!usagesByProperty.has(key)) {
        return;
      }
      const existing = cardsByKey.get(key);
      if (existing) {
        // Same (property, entity type) seen in another file: the first push
        // (user-edited entries first, then AI) supplies the displayed values;
        // this file is recorded so panel edits fan out to its entry too.
        if (!existing.fileIds.some(id => String(id) === String(fileId))) {
          existing.fileIds.push(fileId);
        }
        return;
      }
      const item: NewPropertyItem = {
        propertyName: np.property_name,
        label: np.label,
        fieldType: np.field_type_name,
        entityTypeName: np.entity_type_name,
        fileIds: [fileId],
        options: np.options,
        sourceColumn: np.column,
        usages: usagesByProperty.get(key),
      };
      cardsByKey.set(key, item);
      result.push(item);
    };

    // User-created/edited properties first (takes priority)
    if (userNewPropertiesProp) {
      for (const [fid, fileProps] of Object.entries(userNewPropertiesProp)) {
        for (const np of fileProps) {
          pushIfLive(np, fid);
        }
      }
    }

    // AI-suggested new properties from multi-type suggestions
    for (const file of files) {
      const suggestion = multiTypeSuggestions[file.fileId];
      if (!suggestion) {
        continue;
      }
      for (const et of suggestion.entity_types) {
        for (const np of et.new_properties) {
          pushIfLive(np, file.fileId);
        }
      }
    }

    // AI-suggested new properties from standard (non-multi-type) files
    for (const file of uploadedFiles) {
      if (file.new_properties) {
        for (const np of file.new_properties) {
          pushIfLive(np, file.file_id);
        }
      }
    }

    return result;
  }, [
    files,
    multiTypeSuggestions,
    userNewPropertiesProp,
    uploadedFiles,
    columnMappings,
  ]);

  // Count new properties that need user attention (e.g. select without options).
  const newPropertiesErrorCount = useMemo(
    () =>
      allNewProperties.filter(
        p => p.fieldType === 'select' && (!p.options || p.options.length < 2)
      ).length,
    [allNewProperties]
  );

  // Whether to show the cross-file associations sidebar entry. Only
  // meaningful when there are at least two files with distinct entity types
  // to link between.
  const showAssociations = files.length >= 2 && !!onAssociationMappingsChange;

  // Column lists keyed by string file ID — consumed by CrossFileAssociations.
  const fileColumnsRecord = useMemo(() => {
    const record: Record<string, string[]> = {};
    for (const f of files) {
      record[String(f.fileId)] = f.columns;
    }
    return record;
  }, [files]);

  // Per-file set of CSV columns used as cross-file association keys. Passed
  // to the file mapping view so those columns show a "Key" badge.
  const associationKeyColumnsByFile = useMemo(() => {
    const map: Record<string, Set<string>> = {};
    for (const m of associationMappings ?? []) {
      if (m.source_file_id && m.source_column) {
        const key = String(m.source_file_id);
        (map[key] ??= new Set()).add(m.source_column);
      }
      if (m.target_file_id && m.target_column) {
        const key = String(m.target_file_id);
        (map[key] ??= new Set()).add(m.target_column);
      }
    }
    return map;
  }, [associationMappings]);

  // Single-value form for the mobile <select>. Encodes the active view as
  // either 'newProperties', 'associations', or 'file:<id>'. We round-trip
  // through `files` on change so the original fileId type (string | number)
  // is preserved.
  let mobileSelectValue = '';
  if (activeView?.kind === 'newProperties') {
    mobileSelectValue = 'newProperties';
  } else if (activeView?.kind === 'associations') {
    mobileSelectValue = 'associations';
  } else if (activeView?.kind === 'file') {
    mobileSelectValue = `file:${activeView.fileId}`;
  }

  const mobileSelectItems = useMemo(() => {
    const items: { label: string; id: string }[] = files.map(file => {
      const entityTypeLabel = getEntityTypeLabel(file.entityTypeName);
      const isMultiType =
        (renderedTypesByFile[String(file.fileId)]?.size ?? 0) > 1;
      return {
        label: `${file.filename} · ${isMultiType ? 'Multi-type' : entityTypeLabel}`,
        id: `file:${file.fileId}`,
      };
    });
    if (showAssociations) {
      const count = associationMappings?.length ?? 0;
      items.push({
        label: count > 0 ? `Associations (${count})` : 'Associations',
        id: 'associations',
      });
    }
    if (allNewProperties.length > 0) {
      items.push({
        label:
          newPropertiesErrorCount > 0
            ? `⚠ New properties (${newPropertiesErrorCount} needs attention)`
            : `New properties (${allNewProperties.length})`,
        id: 'newProperties',
      });
    }
    return items;
  }, [
    files,
    renderedTypesByFile,
    showAssociations,
    associationMappings?.length,
    allNewProperties.length,
    newPropertiesErrorCount,
  ]);

  const handleMobileSelectChange = useCallback(
    (key: Key | null) => {
      if (key == null) {
        return;
      }
      const value = String(key);
      if (value === 'newProperties') {
        setActiveView({ kind: 'newProperties' });
        return;
      }
      if (value === 'associations') {
        setActiveView({ kind: 'associations' });
        return;
      }
      if (value.startsWith('file:')) {
        const idStr = value.slice(5);
        const file = files.find(f => String(f.fileId) === idStr);
        if (file) {
          setActiveView({ kind: 'file', fileId: file.fileId });
        }
      }
    },
    [files]
  );

  // Per-file count of columns that have a real mapping (i.e. not skipped or
  // unmapped). Used as a lightweight readiness indicator in the sidebar.
  const fileMappedCount = useCallback(
    (file: FileColumnInfo): number => {
      const suggestion = multiTypeSuggestions[file.fileId];
      if (suggestion) {
        let count = 0;
        for (const et of suggestion.entity_types) {
          const virtualKey = `${file.fileId}_virtual_${et.entity_type_name}`;
          const current = columnMappings[virtualKey] ?? {};
          count += Object.values(current).filter(
            v => v && v !== SKIP_MARKER
          ).length;
        }
        return count;
      }
      const mappings = columnMappings[String(file.fileId)] ?? {};
      const propertyCount = Object.values(mappings).filter(
        v => v && v !== SKIP_MARKER
      ).length;
      // Columns mapped as "Link to record" or "Stage" have their property
      // mapping cleared and instead carry a crmEntityAssociationMappings entry
      // (stage is a stage-typed entry there too, see FileMappingSection) —
      // count them as mapped too.
      const linkCount = crmEntityAssociationMappings.filter(
        m => String(m.source_file_id) === String(file.fileId)
      ).length;
      return propertyCount + linkCount;
    },
    [columnMappings, multiTypeSuggestions, crmEntityAssociationMappings]
  );

  // onCreateProperty is passed straight through to MultiTypeMapping /
  // FileMappingSection: each section binds its OWN mapping key (the multi-type
  // virtual editKey or the plain file id). Binding the active file's plain id
  // here instead was the orphaned-new-property bug — the column mapping was
  // written under a key the multi-type rows (and submit) never read.

  // Resolver for the panel handlers, keyed by
  // propertyIndexKey(propertyName, entityTypeName). Derived from
  // allNewProperties — the exact deduped list the panel renders — so the
  // resolver and the panel can never disagree about which files back a card or
  // what its current label/type is.
  const newPropertyByKey = useMemo(() => {
    const map = new Map<string, NewPropertyItem>();
    for (const item of allNewProperties) {
      map.set(propertyIndexKey(item.propertyName, item.entityTypeName), item);
    }
    return map;
  }, [allNewProperties]);

  // Panel-level property change handler for NewPropertiesPanel. Resolves the
  // card's current values and backing files off the panel's own item and
  // delegates field type, label, and options changes to the parent callbacks,
  // once per backing file. The edit handlers scope every write to a single
  // (fileId, property, entity type) entry, and the import creates ONE property
  // per (property, entity type) from the first file's declaration — so an edit
  // that landed on only one file's entry could be silently overridden at
  // submit by another file's untouched copy. Fanning out keeps every file's
  // entry equal to what the card shows.
  const handlePanelPropertyChange = useCallback(
    (
      propertyName: string,
      entityTypeName: string,
      values: PropertyFormValues
    ) => {
      const item = newPropertyByKey.get(
        propertyIndexKey(propertyName, entityTypeName)
      );
      if (!item) {
        return;
      }

      for (const fileId of item.fileIds) {
        // Only forward the field-type change when it ACTUALLY changed. A blind
        // forward re-fires suggest-select-options for a select property (which
        // then clobbers the user's options) — see #11333 / #11239.
        if (
          onUserNewPropertyFieldTypeChange &&
          values.fieldType !== item.fieldType
        ) {
          onUserNewPropertyFieldTypeChange(
            fileId,
            propertyName,
            entityTypeName,
            values.fieldType
          );
        }
        if (onUserNewPropertyLabelChange && values.label !== item.label) {
          // This is a UI-level short-circuit against the panel's own `item`
          // snapshot, avoiding an unnecessary parent-state write when nothing
          // changed. It is intentionally separate from (not a duplicate of)
          // handleUserNewPropertyLabelChange's own no-op guard, which compares
          // against a live ref-backed lookup rather than this possibly-stale
          // `item` — don't "simplify" one of them away in favor of the other.
          // Pass entityTypeName so the write targets the property the panel is
          // editing, not the first same-named property in another entity type.
          onUserNewPropertyLabelChange(
            fileId,
            propertyName,
            entityTypeName,
            values.label
          );
        }
        // Options forward is idempotent and never issues a network request; a
        // label-only edit passes the unchanged options through as a no-op write.
        if (onUserNewPropertyOptionsChange) {
          onUserNewPropertyOptionsChange(
            fileId,
            propertyName,
            entityTypeName,
            values.options
          );
        }
      }
    },
    [
      newPropertyByKey,
      onUserNewPropertyFieldTypeChange,
      onUserNewPropertyLabelChange,
      onUserNewPropertyOptionsChange,
    ]
  );

  // "Generate options from CSV" on a panel card. The fetch inside
  // handleGenerateSelectOptions already aggregates across every file carrying
  // the (property, entity type); only its write is per-file, so fan the call
  // out to each backing file for the same reason as handlePanelPropertyChange.
  const handlePanelGenerateOptions = useCallback(
    (propertyName: string, entityTypeName: string) => {
      const item = newPropertyByKey.get(
        propertyIndexKey(propertyName, entityTypeName)
      );
      if (!item || !onGenerateSelectOptions) {
        return;
      }
      for (const fileId of item.fileIds) {
        onGenerateSelectOptions(fileId, propertyName, entityTypeName);
      }
    },
    [newPropertyByKey, onGenerateSelectOptions]
  );

  // Pre-compute effective IDs and cross-file associations for the active file
  // so the render body doesn't recreate Sets and arrays on every render.
  // Build the detail pane content based on the current sidebar selection.
  // Extracted into a variable to avoid an IIFE inside JSX.
  let detailContent: ReactNode = null;
  if (activeView?.kind === 'file') {
    const activeFile = files.find(f => f.fileId === activeView.fileId);
    if (activeFile) {
      const suggestion = multiTypeSuggestions[activeFile.fileId];

      // Mapping table — always MultiTypeMapping (prerequisite phases ensure every
      // file has a multiTypeSuggestions entry). Defensive guard for missing entry.
      let mappingContent: ReactNode;
      if (!suggestion) {
        mappingContent = (
          <div className='rounded-lg border border-border-secondary p-6 text-center text-sm text-fg-tertiary'>
            Couldn&apos;t load mappings for this file – re-upload to retry.
          </div>
        );
      } else {
        mappingContent = (
          <MultiTypeMapping
            key={activeFile.fileId}
            orgId={orgId}
            fileId={activeFile.fileId}
            filename={activeFile.filename}
            multiTypeSuggestion={suggestion}
            columnMappings={columnMappings}
            onColumnMappingsChange={onColumnMappingsChange}
            columns={activeFile.columns}
            onNewPropertyFieldTypeChange={onNewPropertyFieldTypeChange}
            onCreateProperty={onCreateProperty}
            userNewProperties={userNewPropertiesProp}
            stageMappings={stageMappings}
            onStageMappingsChange={onStageMappingsChange}
            matchColumns={matchColumns}
            onMatchColumnToggle={onMatchColumnToggle}
            fixedValueMappings={fixedValueMappings}
            onFixedValueMappingsChange={onFixedValueMappingsChange}
            crmEntityAssociationMappings={crmEntityAssociationMappings}
            onCrmEntityAssociationMappingsChange={
              onCrmEntityAssociationMappingsChange
            }
            targetTypeOptions={targetTypeOptions}
            importableTypeOptions={importableTypeOptions}
            associationTypes={associationTypes}
            matchesSource={matchesSource}
            matchesTarget={matchesTarget}
            errorPropertiesByFile={analysisErrorProperties}
            onConfigureSelectOptions={handleConfigureSelectOptions}
            onAddEntityType={onAddEntityType}
            associationTypeSelections={associationTypeSelections}
            onAssociationTypeSelectionChange={onAssociationTypeSelectionChange}
            associationKeyColumns={
              associationKeyColumnsByFile[String(activeFile.fileId)]
            }
          />
        );
      }

      // Entity preview: show what the first row would look like as a CRM entity
      const sampleRows = fileSampleRows?.[String(activeFile.fileId)];
      const fileObject = fileObjects?.[String(activeFile.fileId)];
      const fileHeaders =
        fileColumns[activeFile.fileId as unknown as number] ?? [];
      const rowCount = fileRowCountsProp?.[String(activeFile.fileId)];

      let previewContent: ReactNode = null;
      if (suggestion && sampleRows && sampleRows.length > 0) {
        const renderedForActive =
          renderedTypesByFile[String(activeFile.fileId)];
        previewContent = (
          <div className='space-y-3 mt-4'>
            {suggestion.entity_types
              .filter(et => renderedForActive?.has(et.entity_type_name))
              .map(et => {
                const virtualKey = `${activeFile.fileId}_virtual_${et.entity_type_name}`;
                const etMappings = columnMappings[virtualKey] ?? et.mappings;
                return (
                  <EntityPreviewCard
                    key={et.entity_type_name}
                    orgId={orgId}
                    entityTypeName={et.entity_type_name}
                    sampleRows={sampleRows}
                    mappings={etMappings}
                    rowCount={rowCount}
                    csvFile={fileObject}
                    headers={fileHeaders}
                    userNewProperties={
                      userNewPropertiesProp?.[String(activeFile.fileId)]
                    }
                    aiNewProperties={et.new_properties}
                    sourceFileId={virtualKey}
                    crmEntityAssociationMappings={crmEntityAssociationMappings}
                    fixedValueMappings={fixedValueMappings}
                    defaultStage={stageMappings[virtualKey]?.default_stage}
                  />
                );
              })}
          </div>
        );
      }

      detailContent = (
        <div className='space-y-0'>
          <div className='mb-4'>
            <MappingOutcomesLegend canCreateNewProperties={isSchemaAdmin} />
          </div>
          {mappingContent}
          {previewContent}
        </div>
      );
    }
  } else if (activeView?.kind === 'associations') {
    if (onAssociationMappingsChange) {
      detailContent = (
        <CrossFileAssociations
          uploadedFiles={uploadedFiles}
          fileColumns={fileColumnsRecord}
          associationMappings={associationMappings ?? []}
          onAssociationMappingsChange={onAssociationMappingsChange}
          associationTypes={associationTypes}
          matchesSource={matchesSource}
          matchesTarget={matchesTarget}
          unlinkableFileIds={unlinkableFileIds}
        />
      );
    }
  } else if (activeView?.kind === 'newProperties') {
    detailContent = (
      <NewPropertiesPanel
        properties={allNewProperties}
        onPropertyChange={handlePanelPropertyChange}
        initialExpandedProperty={expandPropertyName}
        initialExpandedEntityType={expandEntityTypeName}
        onGenerateOptions={handlePanelGenerateOptions}
        onNavigateToFile={handleSelectFile}
      />
    );
  }

  return (
    // flex-1 + min-h-0 lets MappingStep fill the wizard's fillHeight body so
    // the sidebar pins and only the detail pane scrolls. `h-full` works in a
    // flex-col parent too, but `flex-1` is more explicit about the intent.
    <div className='flex flex-1 flex-col min-h-0'>
      <div className='flex flex-col md:flex-row flex-1 min-h-0 overflow-hidden'>
        {/* Mobile selector — a compact <select> stands in for the desktop
            sidebar so the detail pane gets the full screen width on narrow
            viewports. */}
        <div className='md:hidden shrink-0 border-b border-border-secondary p-3'>
          <label
            htmlFor='mapping-mobile-view-select'
            className='block text-xs font-medium uppercase tracking-wide text-fg-quaternary mb-1'
          >
            Viewing
          </label>
          <Select
            id='mapping-mobile-view-select'
            value={mobileSelectValue}
            onChange={handleMobileSelectChange}
            items={mobileSelectItems}
            size='xs'
            aria-label={vocabulary.groupPickerAriaLabel}
          >
            {item => <SelectItem {...item} />}
          </Select>
        </div>

        {/* Desktop sidebar — one row per file, plus a virtual "Associations"
            entry when there are multiple effective files. Hidden on mobile;
            the <select> above takes its place. */}
        <aside
          className='hidden md:flex md:flex-col w-64 shrink-0 overflow-y-auto border-r border-border-secondary p-3 space-y-0.5'
          aria-label={vocabulary.groupHeading}
        >
          <div className='px-2 py-1 text-xs font-medium uppercase tracking-wide text-fg-quaternary'>
            {vocabulary.groupHeading}
          </div>
          {files.length === 0 &&
            Array.from({ length: 2 }, (_, i) => (
              <div key={i} className='px-2 py-2 space-y-1.5'>
                <Skeleton variant='text' width='80%' height={16} />
                <Skeleton variant='text' width='50%' height={12} />
              </div>
            ))}
          {files.map(file => {
            const isActive =
              activeView?.kind === 'file' && activeView.fileId === file.fileId;
            return (
              <FileSidebarRow
                key={file.fileId}
                fileId={file.fileId}
                filename={file.filename}
                entityTypeLabel={getEntityTypeLabel(file.entityTypeName)}
                isActive={isActive}
                isMultiType={
                  (renderedTypesByFile[String(file.fileId)]?.size ?? 0) > 1
                }
                mappedCount={fileMappedCount(file)}
                totalColumns={file.columns.length}
                onSelect={handleSelectFile}
              />
            );
          })}
          {showAssociations && (
            <>
              <div
                className='mx-2 my-2 h-px bg-border-secondary'
                aria-hidden='true'
              />
              <Button
                type='button'
                color='tertiary'
                size='sm'
                iconLeading={Link01}
                onClick={handleSelectAssociations}
                aria-pressed={activeView?.kind === 'associations'}
                aria-current={
                  activeView?.kind === 'associations' ? 'true' : undefined
                }
                className={cx(
                  'w-full justify-start',
                  activeView?.kind === 'associations'
                    ? 'bg-bg-secondary text-fg-primary font-medium'
                    : 'text-fg-secondary'
                )}
              >
                <span className='flex-1 truncate text-left'>Associations</span>
                {(associationMappings?.length ?? 0) > 0 && (
                  <span className='text-xs rounded-full px-1.5 py-0.5 bg-bg-tertiary text-fg-quaternary'>
                    {associationMappings?.length}
                  </span>
                )}
              </Button>
            </>
          )}
          {allNewProperties.length > 0 && (
            <>
              <div
                className='mx-2 my-2 h-px bg-border-secondary'
                aria-hidden='true'
              />
              <Button
                type='button'
                color='tertiary'
                size='sm'
                iconLeading={PlusCircle}
                onClick={handleSelectNewProperties}
                aria-pressed={activeView?.kind === 'newProperties'}
                aria-current={
                  activeView?.kind === 'newProperties' ? 'true' : undefined
                }
                className={cx(
                  'w-full justify-start',
                  activeView?.kind === 'newProperties'
                    ? 'bg-bg-secondary text-fg-primary font-medium'
                    : 'text-fg-secondary'
                )}
              >
                <span className='flex-1 truncate text-left'>
                  New properties
                </span>
                <span
                  className={cx(
                    'text-xs rounded-full px-1.5 py-0.5',
                    newPropertiesErrorCount > 0
                      ? 'bg-error-50 text-error-700'
                      : 'bg-bg-tertiary text-fg-quaternary'
                  )}
                >
                  {newPropertiesErrorCount > 0
                    ? `${newPropertiesErrorCount} needs attention`
                    : allNewProperties.length}
                </span>
              </Button>
            </>
          )}
        </aside>

        {/* Detail pane — renders the active file's mapping or the cross-file
            associations view. Only this pane scrolls; the sidebar stays
            pinned because it lives in a non-scrolling flex sibling. */}
        <section
          className='flex-1 overflow-y-auto p-4 md:p-6'
          aria-label='Mapping detail'
        >
          {detailContent ?? <MappingDetailSkeleton />}
        </section>
      </div>
    </div>
  );
}
